import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Sun, Zap, Leaf, Trees, FolderCheck, Users } from "lucide-react";
import SEO from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ImpactPage() {
  const [s, setS] = useState(null);

  useEffect(() => {
    axios.get(`${API}/stats/public`).then((r) => setS(r.data)).catch(() => {});
  }, []);

  const kwh = s ? s.kw_deployed * 1700 : 0;
  const co2 = kwh * 0.7;
  const dollars = kwh * 0.30;
  const trees = co2 / 22;

  const tiles = [
    { icon: Sun, label: "Panels installed", value: s?.panels_installed?.toLocaleString() ?? "…" },
    { icon: Zap, label: "MW deployed", value: s ? s.mw_deployed.toFixed(2) : "…", suffix: "MW" },
    { icon: FolderCheck, label: "Projects completed", value: s?.projects_completed ?? "…" },
    { icon: Users, label: "Clients served", value: s?.clients_served ?? "…" },
  ];

  return (
    <div className="min-h-screen bg-[#0A2540] text-white" data-testid="impact-page">
      <SEO
        path="/impact"
        title="PowerBuilt Impact — Solar deployed in Sint Maarten"
        description="Live sustainability breakdown of PowerBuilt's installed solar capacity in Sint Maarten — panels installed, MW deployed and estimated CO₂ avoided."
      />
      <header className="max-w-5xl mx-auto px-6 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition" data-testid="impact-back">
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Back to home</span>
        </Link>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white rounded-md grid place-items-center overflow-hidden">
            <img src="/logo.png" alt="PowerBuilt" className="w-full h-full object-contain p-0.5" />
          </div>
          <div className="leading-tight">
            <div className="font-heading font-bold text-sm">POWERBUILT</div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-12">
        <p className="overline text-[#C9A356]">Our impact, in numbers</p>
        <h1 className="text-4xl md:text-6xl font-heading font-medium tracking-tight leading-[1.05] mt-3 max-w-3xl">
          Every panel we install is one less<br />
          <span className="text-[#C9A356]">barrel of diesel burned.</span>
        </h1>
        <p className="mt-6 text-lg text-slate-300 max-w-2xl">
          Sint Maarten's grid is heavily diesel-backed. Every kilowatt-hour we generate locally
          replaces about 0.7 kg of CO₂ that would otherwise enter our skies. Here's what
          PowerBuilt clients have built together.
        </p>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-12">
          {tiles.map((t, i) => (
            <div key={i} className="rounded-xl border border-white/10 bg-white/5 p-6" data-testid={`impact-tile-${i}`}>
              <div className="flex items-center gap-2 mb-3">
                <t.icon className="w-4 h-4 text-[#C9A356]" />
                <p className="text-[10px] uppercase tracking-[0.2em] text-white/60">{t.label}</p>
              </div>
              <div className="font-heading text-3xl lg:text-4xl tabular-nums">
                {t.value}
                {t.suffix && <span className="text-[#C9A356] text-xl ml-1">{t.suffix}</span>}
              </div>
            </div>
          ))}
        </div>

        {s && (
          <>
            <div className="mt-16">
              <p className="overline text-[#C9A356] mb-3">Estimated environmental impact</p>
              <div className="grid md:grid-cols-3 gap-4">
                <Card icon={Leaf} label="kg CO₂ avoided" value={Math.round(co2).toLocaleString()} suffix="kg" />
                <Card icon={Trees} label="Mature trees, equivalent" value={Math.round(trees).toLocaleString()} />
                <Card icon={Zap} label="Lifetime savings (est.)" value={`$${Math.round(dollars).toLocaleString()}`} />
              </div>
              <p className="text-xs text-white/40 mt-4 max-w-2xl">
                Estimates based on SXM grid carbon intensity (~0.7 kg/kWh) and 1,700 kWh/kW/yr typical
                Caribbean solar yield. Figures grow as we install more — refresh to see live numbers.
              </p>
            </div>

            <div className="mt-16 rounded-2xl bg-gradient-to-br from-[#C9A356]/10 via-emerald-500/10 to-transparent border border-[#C9A356]/30 p-8">
              <h2 className="font-heading text-2xl mb-2">Want to add to these numbers?</h2>
              <p className="text-slate-300 mb-6 max-w-xl">
                Book a free consultation. We'll show you what your roof, your bill, and the SXM sun could do together.
              </p>
              <Link to="/login" className="btn-accent" data-testid="impact-cta">
                Create your account →
              </Link>
            </div>
          </>
        )}
      </main>

      <footer className="border-t border-white/10 mt-12">
        <div className="max-w-5xl mx-auto px-6 py-6 text-xs text-white/40">
          © 2026 PowerBuilt
        </div>
      </footer>
    </div>
  );
}

function Card({ icon: Icon, label, value, suffix }) {
  return (
    <div className="rounded-xl bg-white/5 border border-white/10 p-6">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-[#C9A356]" />
        <p className="text-[10px] uppercase tracking-[0.2em] text-white/60">{label}</p>
      </div>
      <div className="font-heading text-3xl">
        {value}{suffix && <span className="text-[#C9A356] text-xl ml-1">{suffix}</span>}
      </div>
    </div>
  );
}
